import { sha256 } from '@noble/hashes/sha2.js';
import { bytesToHex, hexToBytes, concatBytes } from '@noble/hashes/utils.js';

/**
 * Binary Merkle tree with domain-separated leaves (0x00) and nodes (0x01).
 * An odd node at the end of a level is promoted unchanged (never duplicated),
 * which avoids the duplicate-leaf ambiguity of Bitcoin-style trees.
 */
const LEAF = new Uint8Array([0]);
const NODE = new Uint8Array([1]);

export const EMPTY_ROOT = bytesToHex(sha256(new Uint8Array([2])));

export function leafHash(leafData: Uint8Array): Uint8Array {
  return sha256(concatBytes(LEAF, leafData));
}

function nodeHash(l: Uint8Array, r: Uint8Array): Uint8Array {
  return sha256(concatBytes(NODE, l, r));
}

export function merkleRoot(leaves: Uint8Array[]): string {
  if (leaves.length === 0) return EMPTY_ROOT;
  let level = leaves.map(leafHash);
  while (level.length > 1) level = nextLevel(level);
  return bytesToHex(level[0]);
}

function nextLevel(level: Uint8Array[]): Uint8Array[] {
  const out: Uint8Array[] = [];
  for (let i = 0; i < level.length; i += 2) {
    out.push(i + 1 < level.length ? nodeHash(level[i], level[i + 1]) : level[i]);
  }
  return out;
}

export interface MerkleProof {
  index: number;
  leafCount: number;
  path: { hash: string; side: 'L' | 'R' }[];
}

export function merkleProof(leaves: Uint8Array[], index: number): MerkleProof {
  if (index < 0 || index >= leaves.length) throw new Error('proof index out of range');
  let level = leaves.map(leafHash);
  let i = index;
  const path: MerkleProof['path'] = [];
  while (level.length > 1) {
    const sib = i ^ 1;
    if (sib < level.length) path.push({ hash: bytesToHex(level[sib]), side: sib < i ? 'L' : 'R' });
    level = nextLevel(level);
    i = Math.floor(i / 2);
  }
  return { index, leafCount: leaves.length, path };
}

export function verifyMerkleProof(leafData: Uint8Array, proof: MerkleProof, root: string): boolean {
  let h = leafHash(leafData);
  for (const step of proof.path) {
    const s = hexToBytes(step.hash);
    h = step.side === 'L' ? nodeHash(s, h) : nodeHash(h, s);
  }
  return bytesToHex(h) === root;
}
